import React, { useState, Suspense } from 'react';
import 'normalize.css';
import Screening from './pages';
import AptlyForms from './aptly-forms';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AppUiStateProvider } from 'state';
import EB from 'components/errorBoundary';
import { Provider, ErrorBoundary } from '@rollbar/react';
import Rollbar from 'rollbar';
import ConfirmDialog from 'components/confirmation-dialog';
require('./utils/dateHelpers');
require('./utils/numberHelpers');
require('./utils/stringHelpers');
import i18n from './i18n';
import LocaleContext from './LocaleContext';
import Loading from './components/loading';
import ESignature from './e-signature';
import PortalSearch from './portal-search';
import Knowledge from './knowledge';
import RentalVerificationPage from './pages/rental-verification';
import EmploymentVerificationPage from './pages/employment-verification';
import 'react-datepicker/dist/react-datepicker.css';

const IGNORED_ERRORS = [
  "null is not an object (evaluating 'document.elementFromPoint",
  'reCAPTCHA placeholder element must be an element or id',
  "Cannot read properties of undefined (reading 'push')",
  "undefined is not an object (evaluating 'google.maps.places')",
  'e.unregisterAllEvents is not a function.',
  'MISC ERROR: Failed to load resource: the server responded with a status of 403 (Forbidden) https://www.google.com/recaptcha/api.js'
];

// eslint-disable-next-line no-undef
const STATIC_CONTENT_URL = `${process.env.REACT_APP_STATIC_CONTENT_URL}`;
// eslint-disable-next-line no-undef
const ROLLBAR_ACTIVE = !!`${process.env.REACT_APP_ROLLBAR_KEY}`;
const ROLLBAR_CONFIG = {
  // eslint-disable-next-line no-undef
  accessToken: `${process.env.REACT_APP_ROLLBAR_KEY}`,
  // eslint-disable-next-line no-undef
  environment: `${process.env.REACT_APP_ROLLBAR_ENV}`,
  enabled: ROLLBAR_ACTIVE,
  captureIp: true,
  captureUncaught: true,
  captureUnhandledRejections: true,
  hostSafeList: [window.location.host],
  autoInstrument: {
    log: false
  },
  checkIgnore: function (isUncaught, args, payload) {
    // payload is a json object with the data that is about to be sent to the Rollbar API.
    // it has the structure as can be seen in the Rollbar UI for the occurrence.
    // so, if you see occurrences in the Rollbar UI that you want to ignore, you can use the "raw json" view to see the
    // structure, and then code against that in your checkIgnore function (like this example) to ignore them in the future.
    // Return true to ignore.

    if (IGNORED_ERRORS.some((error) => payload?.body?.trace?.exception?.message?.includes(error))) {
      return true;
    }

    return false;
  }
};

const rollbar = new Rollbar(ROLLBAR_CONFIG);
console.log(`Rollbar reporting on ${window.location.host}`);

const AppContainer = () => {
  const [locale, setLocale] = useState(i18n.language);
  React.useEffect(() => {
    i18n.on('languageChanged', (lng) => setLocale(i18n.language));
  }, []);

  return (
    <AppUiStateProvider>
      <LocaleContext.Provider value={{ locale, setLocale }}>
        <Suspense fallback={<Loading />}>
          <BrowserRouter>
            <Routes>
              <Route path="knowledge/:knowledgeId" element={<Knowledge />} />
              <Route path="document/:documentId/:token" element={<ESignature />} />
              <Route path="rental-verification/:token" element={<RentalVerificationPage />} />
              <Route
                path="employment-verification/:token"
                element={<EmploymentVerificationPage />}
              />
              <Route path="form/:formId/:cardId/:boardId/:action" element={<AptlyForms />} />
              <Route path="form/:formId/:cardId/:boardId" element={<AptlyForms />} />
              <Route path="search/*" element={<PortalSearch />} />
              <Route path="segment/:segmentId/*" element={<PortalSearch />} />
              {/*short version*/}
              <Route path="s/*" element={<PortalSearch />} />
              <Route path="*" element={<Screening />} />
            </Routes>
            <Toaster position="bottom-right" />
            <ConfirmDialog />
          </BrowserRouter>
        </Suspense>
      </LocaleContext.Provider>
    </AppUiStateProvider>
  );
};

function App() {
  return ROLLBAR_ACTIVE ? (
    <Provider instance={rollbar}>
      <ErrorBoundary>
        <EB>
          <AppContainer />
        </EB>
      </ErrorBoundary>
    </Provider>
  ) : (
    <AppContainer />
  );
}

export default App;
